import { ChangeEvent, KeyboardEvent, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { Tooltip } from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import RefreshIcon from '@mui/icons-material/Refresh'
import CloseIcon from '@mui/icons-material/Close'
import { PageArea } from '..'
import { Text, Title } from '../../components/typography'
import { colorPalette } from '../../style/colorPalette'
import AttachmentIcon from '../../components/icons/AttachmentIcon'
import { AiAgentMessageList } from '../../features/AiAgentChat/AiAgentMessageList'
import { useAiAgentConversation } from '../../features/AiAgentChat/hooks/useAiAgentConversation'
import { useAiAgentImageUpload } from '../../features/AiAgentChat/hooks/useAiAgentImageUpload'
import {
  AttachmentPreviewItem,
  AttachmentsPreviewRow,
  AttachmentThumb,
  AttachmentUploadingOverlay,
  ChatCard,
  ChatColumn,
  ChatHeader,
  ChatInput,
  HeaderIconButton,
  HeaderTexts,
  InlineError,
  InputArea,
  RemoveAttachmentButton,
  SendButton,
} from './styles'

export const AiAgentChatPage = () => {
  const intl = useIntl()
  const [inputValue, setInputValue] = useState('')
  const { messages, isStreaming, inlineError, sendMessage, resetConversation } =
    useAiAgentConversation({
      greetingText: intl.formatMessage({ id: 'aiAgentChat.greeting' }),
      busyErrorText: intl.formatMessage({ id: 'aiAgentChat.error.busy' }),
      generalErrorText: intl.formatMessage({ id: 'aiAgentChat.error.general' }),
      streamErrorText: intl.formatMessage({
        id: 'aiAgentChat.error.streaming',
      }),
    })
  const {
    attachments,
    errorMessage: attachErrorMessage,
    isUploading,
    addFiles,
    removeAttachment,
    clearAttachments,
    getUploadedImageRefs,
  } = useAiAgentImageUpload()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleSendMessage = () => {
    if (isStreaming || inputValue.trim() === '' || isUploading) {
      return
    }
    const messageText = inputValue
    const imageRefs = getUploadedImageRefs()
    setInputValue('')
    // チャット専用ページには周辺文脈（プロジェクト・WBS等）が存在しないため screen のみ送る。
    sendMessage(
      messageText,
      { screen: 'chat' },
      imageRefs.length > 0 ? imageRefs : undefined
    )
    clearAttachments()
  }

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value)
  }

  const handleAttachClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      addFiles(Array.from(event.target.files))
    }
    event.target.value = ''
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (
      event.key === 'Enter' &&
      !event.shiftKey &&
      !event.nativeEvent.isComposing
    ) {
      event.preventDefault()
      handleSendMessage()
    }
  }

  const handleRefresh = () => {
    resetConversation()
    clearAttachments()
  }

  return (
    <PageArea>
      <ChatColumn>
        <ChatCard>
          <ChatHeader>
            <HeaderTexts>
              <Title variant="l">
                {intl.formatMessage({ id: 'aiAgentChat.title' })}
              </Title>
              <Text variant="2s" color={colorPalette.monotone[5]}>
                {intl.formatMessage({ id: 'aiAgentChat.description' })}
              </Text>
            </HeaderTexts>
            <Tooltip title={intl.formatMessage({ id: 'aiAgentChat.reset' })}>
              <span>
                <HeaderIconButton
                  size="small"
                  onClick={handleRefresh}
                  disabled={isStreaming}
                >
                  <RefreshIcon fontSize="small" />
                </HeaderIconButton>
              </span>
            </Tooltip>
          </ChatHeader>
          <AiAgentMessageList messages={messages} isStreaming={isStreaming} />
          {inlineError && <InlineError>{inlineError}</InlineError>}
          {attachErrorMessage && (
            <InlineError>{attachErrorMessage}</InlineError>
          )}
          {attachments.length > 0 && (
            <AttachmentsPreviewRow>
              {attachments.map(attachment => (
                <AttachmentPreviewItem
                  key={attachment.id}
                  hasError={attachment.status === 'error'}
                >
                  <AttachmentThumb src={attachment.previewUrl} alt="" />
                  {attachment.status === 'uploading' && (
                    <AttachmentUploadingOverlay />
                  )}
                  <RemoveAttachmentButton
                    size="small"
                    onClick={() => removeAttachment(attachment.id)}
                    aria-label={intl.formatMessage({
                      id: 'aiAgentChat.attach.remove',
                    })}
                  >
                    <CloseIcon fontSize="inherit" />
                  </RemoveAttachmentButton>
                </AttachmentPreviewItem>
              ))}
            </AttachmentsPreviewRow>
          )}
          <InputArea>
            <Tooltip
              title={intl.formatMessage({ id: 'aiAgentChat.attach.button' })}
            >
              <span>
                <HeaderIconButton
                  size="small"
                  onClick={handleAttachClick}
                  disabled={isStreaming}
                >
                  <AttachmentIcon fontSize="small" />
                </HeaderIconButton>
              </span>
            </Tooltip>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple={true}
              hidden={true}
              onChange={handleFileInputChange}
            />
            <ChatInput
              placeholder={intl.formatMessage({
                id: 'aiAgentChat.input.placeholder',
              })}
              fullWidth={true}
              multiline={true}
              maxRows={6}
              inputProps={{
                'aria-label': intl.formatMessage({
                  id: 'aiAgentChat.input.placeholder',
                }),
              }}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              disabled={isStreaming}
            />
            <Tooltip title={intl.formatMessage({ id: 'aiAgentChat.send' })}>
              <span>
                <SendButton
                  onClick={handleSendMessage}
                  disabled={
                    isStreaming || inputValue.trim() === '' || isUploading
                  }
                >
                  <SendIcon />
                </SendButton>
              </span>
            </Tooltip>
          </InputArea>
        </ChatCard>
      </ChatColumn>
    </PageArea>
  )
}
